import { Component, Inject, ChangeDetectionStrategy, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { Brand } from '../../interfaces/brands/brand';
import { BrandForCreation } from '../../interfaces/brands/brand-for-creation';
import { BrandForUpdate } from '../../interfaces/brands/brand-for-update';
import { DocumentBinary } from '../../interfaces/documents/document-binary';
import { CorporationRepositoryService } from '../../services/corporation/corporation-repository-service.service';
import { Base64Service } from '../../services/base-64/base64-service';
import { TranslocoService } from '@jsverse/transloco';

export interface BrandFormDialogData {
  corporationID: string;
  brand?: Brand | null;
  currentLogoUrl?: string | null;
}

@Component({
  selector: 'lib-brand-form-dialog',
  templateUrl: './brand-form-dialog.component.html',
  styleUrls: ['./brand-form-dialog.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class BrandFormDialogComponent {
  private readonly transloco = inject(TranslocoService);
  form: FormGroup;
  saving = false;
  isEdit: boolean;

  uploadedLogoFileName: string | null = null;
  uploadedLogoBase64: string | null = null;

  constructor(
    private formBuilder: FormBuilder,
    private corporationRepo: CorporationRepositoryService,
    private base64Service: Base64Service,
    private toastr: ToastrService,
    private dialogRef: MatDialogRef<BrandFormDialogComponent, boolean>,
    @Inject(MAT_DIALOG_DATA) public data: BrandFormDialogData) {
    this.isEdit = !!data.brand;
    this.form = this.formBuilder.group({
      name: [data.brand?.name ?? '', Validators.required],
      hexCode: [data.brand?.hexCode ?? '']
    });
  }

  // ─── View helpers ─────────────────────────────────────────────────────────────

  get swatchPreview(): string | null {
    const hex = this.normalizeHex(this.form.get('hexCode')?.value);
    return hex ? '#' + hex : null;
  }

  get previewLogoUrl(): string | null {
    if (this.uploadedLogoBase64 && this.uploadedLogoFileName) {
      const mime = this.getDocumentType(this.uploadedLogoFileName) === 'JPG' ? 'image/jpeg' : 'image/png';
      return `data:${mime};base64,${this.uploadedLogoBase64}`;
    }

    return this.data.currentLogoUrl ?? null;
  }

  // ─── Logo upload ─────────────────────────────────────────────────────────────

  onLogoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      return;
    }

    this.uploadedLogoFileName = this.sanitizeFileName(file.name);
    this.base64Service.convertToBase64(file).subscribe(base64 => {
      this.uploadedLogoBase64 = base64;

      // Best-guess a brand colour from the logo, but only if the user hasn't set one.
      if (!this.hasValidHex()) {
        const mime = this.getDocumentType(file.name) === 'JPG' ? 'image/jpeg' : 'image/png';
        this.extractDominantHexFromImage(`data:${mime};base64,${base64}`).then(hex => {
          this.form.get('hexCode')?.setValue(hex.toUpperCase());
        });
      }
    });
  }

  private hasValidHex(): boolean {
    const value = this.form.get('hexCode')?.value as string | null | undefined;
    return this.normalizeHex(value) != null;
  }

  /**
   * Estimates a representative brand colour from a logo image: averages the saturated,
   * mid-lightness pixels, then darkens until it has >= 4.5:1 contrast on white (WCAG AA).
   */
  private extractDominantHexFromImage(dataUrl: string): Promise<string> {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const size = 64;
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d')!;
        ctx.drawImage(img, 0, 0, size, size);
        const { data } = ctx.getImageData(0, 0, size, size);

        let rSum = 0, gSum = 0, bSum = 0, count = 0;
        for (let i = 0; i < data.length; i += 4) {
          const alpha = data[i + 3];
          if (alpha < 128) continue;
          const r = data[i], g = data[i + 1], b = data[i + 2];
          const max = Math.max(r, g, b), min = Math.min(r, g, b);
          const saturation = max === 0 ? 0 : (max - min) / max;
          const lightness = (max + min) / 510;
          if (saturation < 0.15 || lightness > 0.95 || lightness < 0.05) continue;
          rSum += r; gSum += g; bSum += b; count++;
        }

        let r = count === 0 ? 0 : Math.round(rSum / count);
        let g = count === 0 ? 114 : Math.round(gSum / count);
        let b = count === 0 ? 174 : Math.round(bSum / count);

        // Darken until >= 4.5:1 contrast with white (WCAG 2.1 AA)
        const linearCh = (c: number) => { const s = c / 255; return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4); };
        const contrastW = (r: number, g: number, b: number) => 1.05 / (0.2126 * linearCh(r) + 0.7152 * linearCh(g) + 0.0722 * linearCh(b) + 0.05);
        if (contrastW(r, g, b) < 4.5) {
          let lo = 0, hi = 1;
          for (let i = 0; i < 20; i++) {
            const mid = (lo + hi) / 2;
            contrastW(r * mid, g * mid, b * mid) >= 4.5 ? (lo = mid) : (hi = mid);
          }
          r = Math.round(r * lo); g = Math.round(g * lo); b = Math.round(b * lo);
        }

        resolve([r, g, b].map(x => x.toString(16).padStart(2, '0')).join(''));
      };
      img.onerror = () => resolve('0072ae');
      img.src = dataUrl;
    });
  }

  // ─── Save / cancel ───────────────────────────────────────────────────────────

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const logoDocument = this.buildLogoDocument();
    const hexCode = this.normalizeHex(this.form.get('hexCode')?.value);
    const name = this.form.get('name')?.value;

    this.saving = true;

    if (this.isEdit && this.data.brand) {
      const update: BrandForUpdate = { name, hexCode, logoDocument };
      this.corporationRepo.updateBrand(this.data.corporationID, this.data.brand.id, update).subscribe({
        next: () => this.onSaved(this.transloco.translate('brandFormDialog.brandUpdated')),
        error: () => this.onError(this.transloco.translate('brandFormDialog.failedToUpdateBrand'))
      });
    } else {
      const create: BrandForCreation = { name, hexCode, logoDocument };
      this.corporationRepo.createBrand(this.data.corporationID, create).subscribe({
        next: () => this.onSaved(this.transloco.translate('brandFormDialog.brandCreated')),
        error: () => this.onError(this.transloco.translate('brandFormDialog.failedToCreateBrand'))
      });
    }
  }

  cancel(): void {
    this.dialogRef.close(false);
  }

  private onSaved(message: string): void {
    this.saving = false;
    this.toastr.success(message);
    this.dialogRef.close(true);
  }

  private onError(message: string): void {
    this.saving = false;
    this.toastr.error(message);
  }

  // ─── Internals ─────────────────────────────────────────────────────────────────

  private buildLogoDocument(): DocumentBinary | null {
    if (!this.uploadedLogoBase64 || !this.uploadedLogoFileName) {
      return null;
    }

    return {
      fileName: this.uploadedLogoFileName,
      documentTypeID: this.getDocumentType(this.uploadedLogoFileName),
      base64: this.uploadedLogoBase64
    };
  }

  private normalizeHex(value: string | null | undefined): string | null {
    if (!value) {
      return null;
    }

    const stripped = value.startsWith('#') ? value.slice(1) : value;
    return /^[0-9A-Fa-f]{6}$/.test(stripped) ? stripped.toLowerCase() : null;
  }

  private getDocumentType(fileName: string): string {
    const lower = fileName.toLowerCase();
    if (lower.endsWith('.jpg') || lower.endsWith('.jpeg')) {
      return 'JPG';
    }
    return 'PNG';
  }

  // collapse weird Unicode characters to their ASCII equivalents and sanitize.
  // Doing this because multiple periods were causing problems and going a bit overboard defensively.
  private sanitizeFileName(name: string): string {
    const lastDot = name.lastIndexOf('.');
    const base = lastDot > 0 ? name.slice(0, lastDot) : name;
    const ext = lastDot > 0 ? name.slice(lastDot).toLowerCase() : '';
    const cleanBase = base
      .normalize('NFKC')
      .replace(/[^\w.\-]+/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '');
    return (cleanBase || 'logo') + ext;
  }
}
